import React from 'react'
import type { VehicleInfo, SlotId, DragState } from './types'
import { UPPER_SLOTS, LOWER_SLOTS, SLOT_META, vehicleLabel, vehicleColorHex } from './types'

interface TrailerCanvasProps {
  /** slotId → vehicle placed there */
  assignments: Map<SlotId, VehicleInfo>
  /** which slot is hovered by a dragging vehicle */
  hoveredSlot: SlotId | null
  /** active drag state (for source highlighting) */
  drag: DragState | null
  /** called when pointer goes down on a placed vehicle */
  onVehicleDragStart: (e: React.PointerEvent, vehicleId: string, sourceSlotId: SlotId) => void
  selectedVehicleId?: string | null
  selectedSlotId?: SlotId | null
  onSelectVehicle?: (vehicleId: string) => void
  onSelectSlot?: (slotId: SlotId) => void
  readOnly?: boolean
}

function SlotCell({
  slotId, vehicle, isHovered, isDragSource, onDragStart, selectedVehicleId, selectedSlotId, onSelectVehicle, onSelectSlot, readOnly,
}: {
  slotId: SlotId
  vehicle: VehicleInfo | undefined
  isHovered: boolean
  isDragSource: boolean
  onDragStart: (e: React.PointerEvent, vehicleId: string, slotId: SlotId) => void
  selectedVehicleId?: string | null
  selectedSlotId?: SlotId | null
  onSelectVehicle?: (vehicleId: string) => void
  onSelectSlot?: (slotId: SlotId) => void
  readOnly: boolean
}) {
  const meta = SLOT_META[slotId]
  const color = vehicleColorHex(vehicle?.color)
  const occupied = !!vehicle && !isDragSource
  const isSelectedSlot = selectedSlotId === slotId
  const isSelectedVehicle = selectedVehicleId != null && vehicle?.vehicleId === selectedVehicleId

  return (
    <div
      data-slot-id={slotId}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? -1 : 0}
      aria-label={`${meta.label} ${slotId}`}
      aria-pressed={isSelectedSlot}
      className={[
        'relative flex flex-col items-center justify-center rounded-lg border-2 transition-all duration-100 select-none',
        'min-h-[7rem]',
        occupied
          ? isSelectedSlot
            ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200 shadow-sm'
            : 'bg-white border-blue-400 shadow-sm'
          : isHovered
          ? isSelectedSlot
            ? 'bg-blue-100 border-blue-500 border-dashed scale-[1.03] shadow-lg'
            : 'bg-blue-50 border-blue-400 border-dashed scale-[1.03] shadow-lg'
          : 'bg-gray-800/40 border-gray-600 border-dashed',
      ].join(' ')}
      onClick={readOnly ? undefined : () => onSelectSlot?.(slotId)}
      onKeyDown={readOnly ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelectSlot?.(slotId)
        }
      }}
    >
      {/* Slot ID badge */}
      <span className="absolute top-1 left-1.5 text-[9px] font-mono text-gray-500 leading-none select-none">
        {slotId}
      </span>

      {/* Deck label */}
      <span className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 select-none ${occupied ? 'text-gray-400' : 'text-gray-500'}`}>
        {meta.label}
      </span>

      {occupied && vehicle ? (
        /* Placed vehicle card */
        <button
          type="button"
          className={[
            'absolute inset-1.5 rounded-md flex flex-col items-center justify-center px-1 py-1 min-h-[44px] w-[calc(100%-0.75rem)]',
            readOnly ? 'cursor-default' : isSelectedVehicle ? 'cursor-grab active:cursor-grabbing touch-none ring-2 ring-blue-300' : 'cursor-grab active:cursor-grabbing touch-none',
            'select-none text-left',
          ].join(' ')}
          style={{
            background: `${color}22`,
            borderLeft: `3px solid ${color}`,
          }}
          aria-pressed={isSelectedVehicle}
          onPointerDown={readOnly ? undefined : (e) => {
            if (e.pointerType === 'touch') return
            onDragStart(e, vehicle.vehicleId, slotId)
          }}
          onClick={readOnly ? undefined : (e) => {
            e.stopPropagation()
            onSelectVehicle?.(vehicle.vehicleId)
          }}
        >
          <div className="text-xs font-semibold text-gray-800 text-center leading-tight line-clamp-2">
            {vehicleLabel(vehicle)}
          </div>
          <div className="text-[9px] text-gray-500 font-mono mt-0.5">…{vehicle.vin.slice(-6)}</div>
          {!readOnly && (
            <div className="text-[8px] text-gray-400 mt-0.5">drag to move</div>
          )}
        </button>
      ) : isDragSource ? (
        <span className="text-xs text-blue-400 animate-pulse">moving…</span>
      ) : isHovered ? (
        <span className="text-xs text-blue-400 font-medium">Drop here</span>
      ) : (
        <span className="text-xs text-gray-600">Empty</span>
      )}
    </div>
  )
}

export function TrailerCanvas({ assignments, hoveredSlot, drag, onVehicleDragStart, selectedVehicleId, selectedSlotId, onSelectVehicle, onSelectSlot, readOnly = false }: TrailerCanvasProps) {
  return (
    <div className="bg-gray-900 rounded-2xl p-4 space-y-3 shadow-xl">
      {/* Direction indicator */}
      <div className="flex justify-between text-[10px] text-gray-500 px-1 font-mono">
        <span>◀ CAB / FRONT</span>
        <span>REAR / RAMP ▶</span>
      </div>

      {/* Upper deck */}
      <div>
        <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest mb-1.5 px-1">
          Upper Deck
        </div>
        <div className="grid grid-cols-4 gap-2">
          {UPPER_SLOTS.map(slotId => (
            <SlotCell
              key={slotId}
              slotId={slotId}
              vehicle={assignments.get(slotId)}
              isHovered={hoveredSlot === slotId && !!drag}
              isDragSource={drag?.sourceSlotId === slotId}
              onDragStart={onVehicleDragStart}
              selectedVehicleId={selectedVehicleId}
              selectedSlotId={selectedSlotId}
              onSelectVehicle={onSelectVehicle}
              onSelectSlot={onSelectSlot}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      {/* Deck separator */}
      <div className="border-t border-gray-700 border-dashed mx-2" />

      {/* Lower deck */}
      <div>
        <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest mb-1.5 px-1">
          Lower Deck
        </div>
        <div className="grid grid-cols-4 gap-2">
          {LOWER_SLOTS.map(slotId => (
            <SlotCell
              key={slotId}
              slotId={slotId}
              vehicle={assignments.get(slotId)}
              isHovered={hoveredSlot === slotId && !!drag}
              isDragSource={drag?.sourceSlotId === slotId}
              onDragStart={onVehicleDragStart}
              selectedVehicleId={selectedVehicleId}
              selectedSlotId={selectedSlotId}
              onSelectVehicle={onSelectVehicle}
              onSelectSlot={onSelectSlot}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 pt-1 text-[9px] text-gray-600">
        <span>● Col 1–4: Front → Rear</span>
        <span>● Row 1: Upper  Row 2: Lower</span>
      </div>
    </div>
  )
}
